import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faSearch,
  faList,
  faSignOutAlt,
  faSignInAlt,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { NavLink, useOutletContext, useLocation } from 'react-router-dom';
import AuthContext from './AuthContext';
import './NavOverride.css';

const docinxBase = process.env.REACT_APP_DOCINX_LINK;

const Nav = ({ dataClient: propDataClient }) => {
  const { user, logout } = useContext(AuthContext);
  const outletContext = useOutletContext() || {};
  const dataClient = propDataClient || outletContext.dataClient || null;

  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => sessionStorage.getItem('activeNavSection') || '');

  const pathname = window.location.pathname;
  const isNotFound = pathname === '/not-found'; // exact match
  const storedCustomer = localStorage.getItem('customer_number');

  const customerId = isNotFound
    ? storedCustomer || 'unknown'
    : (
        propDataClient?.customer_number ||
        outletContext?.dataClient?.customer_number ||
        storedCustomer ||
        'unknown'
      );

  const taxRoutes = useMemo(() => ([
    `/customer/${customerId}/taxSearch`,
    `/customer/${customerId}/taxSearchResults`,
    `/customer/${customerId}/taxStatus`,
    `/customer/${customerId}/propertyDetail`,
    `/customer/${customerId}/taxDetail`,
    `/customer/${customerId}/billImage`,
    `/customer/${customerId}/TaxRoll`,
  ]), [customerId]);

  const paymentsRoutes = useMemo(() => ([
    `/customer/${customerId}/paymentsSearch`,
    `/customer/${customerId}/paymentsSearchResults`,
    `/customer/${customerId}/propertyPayments`,
  ]), [customerId]);

  const historyPrefix = useMemo(() => `/customer/${customerId}/history`, [customerId]);
  const clientPrefix = useMemo(() => `/customer/${customerId}/client`, [customerId]);
  const userPrefix = useMemo(() => `/customer/${customerId}/user`, [customerId]);

  const startsWithAny = (path, prefixes) => prefixes.some((p) => path.startsWith(p));
 
  const apps = typeof dataClient?.products === 'string'
    ? dataClient.products.split(',').map((app) => app.trim())
    : [];

  const roleName = localStorage.getItem('role_name');
  const userId = localStorage.getItem('id');
  // Helper function to check if an app is available
  const hasApp = (appName) => apps.includes(appName);

  const homePageUrl =
    dataClient?.home_page ||
    propDataClient?.home_page ||
    outletContext?.dataClient?.home_page ||
    null;

  const normalizedHomePageUrl =
  homePageUrl && !/^https?:\/\//i.test(homePageUrl)
    ? `https://${homePageUrl}`
    : homePageUrl;

  // Helper function to render a menu item
  const renderMenuItem = (to, label, icon, onClick = null, sectionKey = null) => (
    <ul>
      <li>
        <NavLink
          to={to}
          className={({ isActive }) => {
            // If this item participates in section-highlighting (tax/payments), use activeSection.
            if (sectionKey) {
              return activeSection === sectionKey ? 'nav-item active' : 'nav-item';
            }

            // Otherwise, use normal router active behavior (My Profile, Client List, User List, Login, etc.)
            return isActive ? 'nav-item active' : 'nav-item';
          }}
          onClick={(e) => {
            if (sectionKey) {
              sessionStorage.setItem('activeNavSection', sectionKey);
              setActiveSection(sectionKey);
            } else {
              // Optional but recommended: clear sticky section so it doesn't "double highlight"
              // when you go to non-section pages like Profile or Admin lists.
              sessionStorage.removeItem('activeNavSection');
              setActiveSection('');
            }

            if (onClick) onClick(e);
          }}
        >
          <FontAwesomeIcon icon={icon} className="iconGapWide" />
          {label}
        </NavLink>
      </li>
    </ul>
  );

  // Helper function to render an EXTERNAL menu item (opens new tab)
  const renderExternalMenuItem = (href, label, icon) => (
    <ul>
      <li>
        <a
          href={href}
          className="nav-item"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={icon} className="iconGapWide" />
          {label}
        </a>
      </li>
    </ul>
  );

  useEffect(() => {
  const path = location.pathname;

    if (startsWithAny(path, taxRoutes)) {
      sessionStorage.setItem('activeNavSection', 'tax');
      setActiveSection('tax');
      return;
    }

    if (startsWithAny(path, paymentsRoutes)) {
      sessionStorage.setItem('activeNavSection', 'payments');
      setActiveSection('payments');
      return;
    }

    if (path.startsWith(clientPrefix)) {
      sessionStorage.setItem('activeNavSection', 'client');
      setActiveSection('client');
      return;
    }

    if (path.startsWith(userPrefix)) {
      sessionStorage.setItem('activeNavSection', 'user');
      setActiveSection('user');
      return;
    }

    // history is shared: don't overwrite; keep last activeSection
    if (path.startsWith(historyPrefix)) {
      const from = location.state?.fromSection; // 'tax' or 'payments'
      if (from === 'tax' || from === 'payments') {
        sessionStorage.setItem('activeNavSection', from);
        setActiveSection(from);
      } else {
        const saved = sessionStorage.getItem('activeNavSection') || '';
        setActiveSection(saved);
      }
      return;
    }
  }, [location.pathname, location.state, taxRoutes, paymentsRoutes, historyPrefix]);

  if (!customerId || customerId === 'unknown') {
  return (
    <nav>
      <div className="navContent">
        <div className="left_navigation">
          <div id="nav-menu">
            <ul><li><span className="nav-item">Navigation unavailable</span></li></ul>
          </div>
        </div>
      </div>
    </nav>
  );
}

  return (
    <nav>
      <div className="navContent">
        <div className="left_navigation">
          <div id="nav-menu">

            {/* Home link (opens customer.home_page in new tab) */}
            {normalizedHomePageUrl && renderExternalMenuItem(normalizedHomePageUrl, 'Home', faHome)}
            
            {/* Tax Search link */}
            {hasApp('tax') && renderMenuItem(`/customer/${customerId}/taxSearch`, 'Tax Search', faSearch, null, 'tax' )}

            {/* Doc Search link */}
            {hasApp('doc') &&
            renderMenuItem(
              `${docinxBase}customer_id=${customerId}&current_app=doc&header_text=Document%20Search&body_include=doc_search_form`,
              'Doc Search',
              faSearch
            )}

            {/* Tax Payments link */}
            {hasApp('tpay') && renderMenuItem( `/customer/${customerId}/paymentsSearch`, 'Tax Payments', faDollarSign, null, 'payments' )}

            {/* Client List: Only for SuperAdmin and Admin */}
            {(roleName === 'SuperAdmin' || roleName === 'Admin') &&
              renderMenuItem(`/customer/${customerId}/client/list`, 'Client List', faList, null, 'client')}

            {/* User List: Only for SuperAdmin and Admin */}
            {(roleName === 'SuperAdmin' || roleName === 'Admin') &&
              renderMenuItem(`/customer/${customerId}/user/list`, 'User List', faList, null, 'user')}

            {/* My Profile: Only for non-Admin, non-SuperAdmin roles */}
            {roleName !== 'SuperAdmin' && roleName !== 'Admin' && user &&
              renderMenuItem(`/customer/${customerId}/user/manage/${userId}`, 'My Profile', faList, null, 'user')}

            {/* Logout or Login link */}
            {user
              ? renderMenuItem(
                  '/logout',
                  'Logout',
                  faSignOutAlt,
                  (e) => {
                    e.preventDefault();
                    logout();
                  }
                )
              : renderMenuItem(`/customer/${customerId}/login`, 'Login', faSignInAlt)}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav;